import { computed, ref } from 'vue'
import { freezeObject, isFunction } from '@/global/utils/utils'
import TabActionButtons from '@/global/components/view-layouts/tabs-layout/components/TabActionButtons.vue'
import BaseDetailsInfo from '@/modules/road-maintenance-module/winter-service-v2/bases/components/BaseDetailsInfo.vue'
import { useBaseFields } from '@/modules/road-maintenance-module/winter-service-v2/bases/composables/useBaseFields'
import {
  pickedBasePhoto,
  toBasePayload,
  uploadBasePhoto
} from '@/modules/road-maintenance-module/winter-service-v2/bases/composables/baseData'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  BASES_ENDPOINT,
  BASES_TRANSLATION_NAMESPACE as NS,
  BASE_DETAILS_ROUTE
} from '@/modules/road-maintenance-module/winter-service-v2/bases/config/constants'

const TAB_KEY = 'base_create'

export function useBaseCreate (options) {
  const {
    translate,
    router,
    openGeolocationDialogFn,
    onCancelCreate
  } = options || {}

  const { fields } = useBaseFields({
    translate,
    openGeolocationDialogFn
  })

  // The photo endpoint hangs under a base, so a photo picked while creating one can
  // only be sent once the base exists - it is remembered here until then.
  const pendingPhoto = ref(null)

  const apiConfig = freezeObject({
    post: {
      module: WINTER_SERVICE_MODULE,
      route: BASES_ENDPOINT
    }
  })

  const onCancelTabSave = (event) => {
    const { cancelTabSave } = event || {}

    if (isFunction(cancelTabSave)) {
      cancelTabSave()
    }

    if (isFunction(onCancelCreate)) {
      onCancelCreate()
    }
  }

  const modifyDataBeforeSend = ({ copiedData, jsonData }) => {
    pendingPhoto.value = pickedBasePhoto(jsonData)

    return toBasePayload(copiedData)
  }

  const uploadPhotoAndRedirect = async (event, data) => {
    const { id } = data || {}
    if (!id) {
      return
    }

    if (pendingPhoto.value) {
      try {
        await uploadBasePhoto(id, pendingPhoto.value)
      }
      catch (exception) {
        // The base is created either way and the api layer has already reported
        // the failed upload - the details screen it lands on shows what stuck.
        console.error(exception)
      }
      finally {
        pendingPhoto.value = null
      }
    }

    router.push({
      name: BASE_DETAILS_ROUTE,
      params: {
        id: String(id)
      }
    })
  }

  const tabActions = computed(() => ({
    component: TabActionButtons,
    title: translate(`${NS}.new_base`),
    showEditButtons: true,
    onCancelTabSave
  }))

  // See useBaseDetails - `rows` is what Content.vue reads to build the save
  // payload, so the fields have to live there.
  const infoBlock = computed(() => ({
    component: BaseDetailsInfo,
    visible: true,
    rows: [
      {
        type: 'fields',
        columnsCount: 1,
        fields: fields.value
      }
    ]
  }))

  return computed(() => ({
    containerStyle: {
      padding: 0
    },
    tabs: [
      {
        key: TAB_KEY,
        title: translate(`${NS}.new_base`),
        apiConfig,
        afterSave: uploadPhotoAndRedirect,
        // The status dropdown is hidden while creating, but a payload that does
        // carry one has to speak the backend's `is_active`.
        modifyDataBeforeSend,
        // Coordinates come from the address picker, not from a field of their own.
        hiddenFormKeys: [
          'latitude',
          'longitude'
        ],
        tabContent: [
          tabActions.value,
          infoBlock.value
        ]
      }
    ]
  }))
}
